import React, { useEffect, useRef, useState } from "react";
import { IoIosNotificationsOutline } from "react-icons/io";
import { IoMenuOutline } from "react-icons/io5";
import { useProfile } from "../contexts/ProfileContext";
import { useSidebar } from "../contexts/SidebarContext";
import { useNavigate } from "react-router-dom";
import { FiLogOut } from "react-icons/fi";
import { MdOutlineAddToPhotos, MdOutlineMail, MdOutlinePrivacyTip, MdOutlineVerified, MdVerified } from "react-icons/md";
import { RiCustomerService2Line, RiLockPasswordLine } from "react-icons/ri";
import { FaHourglassHalf, FaRegCreditCard, FaRegEdit } from "react-icons/fa";
import { FcCancel } from "react-icons/fc";
import UserProfile from "../layouts/AuthLayout/UserProfile";
import NotificationPopup from "./NotificationPopup";
import VerificationPopup from "./VerificationPopup";
import VerificationRejectionReason from "./VerificationRejectionReason";
import { getApi } from "../pages/AppApis";

export default function MobileHeader({ screenName = "Find Properties" }) {
    const { profileData, setProfileData } = useProfile();
    const { toggleDrawer } = useSidebar();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false);
    const [verificationPopupVisible, setVerificationPopupVisible] = useState(false);
    const [verificationReason, setVerificationReason] = useState(false);
    const [notificationsDAta, setNotificationsDAta] = useState([]);

    const dropdownRef = useRef(null);

    const getNotificationData = async () => {
        try {
            const response = await getApi("/notifications/user/notifications");
            if (response?.response?.data?.user_notifications) {
                setNotificationsDAta(response?.response?.data?.user_notifications);
            }
        } catch (error) {
            console.log("Error fetching notifications:", error);
        }
    };

    useEffect(() => {
        getNotificationData();
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setIsNotificationPopupOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const logout = () => {
        localStorage.removeItem("authUser");
        localStorage.removeItem("refreshToken");
        sessionStorage.removeItem("authUser");
        sessionStorage.removeItem("refreshToken");
        setProfileData({});
        navigate("/login");
    };

    const truncateName = (name = "") => {
        return name.length > 13 ? name.slice(0, 13) + "..." : name;
    };

    const renderProfilePicture = (profile_pic) => {
        if (profile_pic && profile_pic?.startsWith("http")) {
            return profile_pic;
        } else if (profile_pic.startsWith("/public/images")) {
            return "https://image.onmls.com" + profile_pic;
        } else {
            return `https://image.onmls.com/public/images/${profile_pic}`;
        }
    };

    const isUnReadNotificationAvailable = notificationsDAta?.some((notification) => notification.is_read === false);

    return (
        <div className="tw-w-full tw-flex tw-items-center tw-justify-between tw-bg-tertiary tw-h-[55px] tw-px-3 md:tw-hidden" ref={dropdownRef}>
            {/* Left side: Hamburger Menu + Screen Name */}
            <div className="tw-flex tw-items-center tw-gap-3">
                {/* Hamburger Menu */}
                <button onClick={toggleDrawer} className="tw-flex tw-items-center tw-justify-center">
                    <IoMenuOutline color="#fff" size={32} />
                </button>

                {/* Screen Name */}
                <span className="tw-text-white tw-font-medium tw-text-[16px] tw-text-nowrap">{screenName}</span>
            </div>

            {/* Right side: Notification Icon + Profile Image */}
            <div className="tw-flex tw-items-center tw-gap-3">
                {/* Notification Icon */}
                <button
                    onClick={() => setIsNotificationPopupOpen(!isNotificationPopupOpen)}
                    className="tw-bg-white tw-relative tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center"
                >
                    <IoIosNotificationsOutline size={24} color="black" />
                    {isUnReadNotificationAvailable && (
                        <div className="tw-absolute tw-top-0 tw-right-0 tw-w-[12px] tw-h-[12px] tw-bg-[#DC3545] tw-rounded-full tw-border-[2.5px] tw-border-white"></div>
                    )}
                </button>

                {/* Profile Image */}
                <button onClick={() => setIsOpen(!isOpen)} className="tw-w-10 tw-h-10">
                    {profileData?.profile_pic ? (
                        <img
                            src={renderProfilePicture(profileData?.profile_pic)}
                            alt="User"
                            className="tw-w-10 tw-h-10 tw-border-2 tw-border-white tw-rounded-full"
                        />
                    ) : (
                        <UserProfile name={`${profileData?.first_name} ${profileData?.last_name}`} fallbackImageUrl={profileData?.profile_pic} />
                    )}
                </button>
            </div>

            {/* Profile Dropdown Menu */}
            {isOpen && (
                <div
                    style={{ zIndex: 9999 }}
                    className="tw-absolute tw-text-black tw-overflow-y-auto tw-max-h-[calc(100vh-55px)] tw-top-[55px] tw-mt-2 tw-bg-white tw-rounded-2xl tw-shadow-lg tw-fixed tw-left-[0.5rem] tw-right-[0.5rem] tw-w-auto"
                >
                    {/* User Info */}
                    <div className="tw-relative tw-p-4 tw-flex tw-rounded-lg tw-flex-col tw-items-center">
                        {profileData?.profile_pic ? (
                            <img
                                src={renderProfilePicture(profileData?.profile_pic)}
                                alt="User"
                                className="tw-w-16 tw-h-16 tw-rounded-full tw-border-2 tw-border-gray-200"
                            />
                        ) : (
                            <div className="tw-w-16 tw-h-16 tw-rounded-full tw-border-2 tw-border-gray-200 tw-bg-red-500 tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-xl">
                                {profileData?.first_name?.charAt(0).toUpperCase()}
                                {profileData?.last_name?.charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
                            <p className="tw-font-semibold tw-text-lg">
                                {truncateName(profileData?.first_name)} {truncateName(profileData?.last_name)}
                            </p>
                            {profileData?.verification_status === "approved" ? (
                                <span onClick={() => setVerificationPopupVisible(true)}>
                                    <MdVerified color="#2bba00" />
                                </span>
                            ) : profileData?.verification_status === "pending" ? (
                                <span onClick={() => setVerificationPopupVisible(true)}>
                                    <FaHourglassHalf color="#F2C94C" />
                                </span>
                            ) : profileData?.verification_status === "rejected" ? (
                                <span onClick={() => setVerificationReason(true)}>
                                    <FcCancel color="red" />
                                </span>
                            ) : null}
                        </div>

                        <FaRegEdit
                            onClick={() => {
                                setIsOpen(false);
                                navigate("/profile-detail");
                            }}
                            className="tw-absolute tw-cursor-pointer tw-top-4 tw-right-4"
                        />
                    </div>

                    {/* Menu Items */}
                    <div className="tw-border-t tw-overflow-y-auto tw-text-black tw-border-gray-200">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                navigate("/change-password");
                            }}
                            className="tw-flex tw-items-center tw-border-b-1 tw-gap-2 tw-w-full tw-text-left tw-p-2 tw-text-sm"
                        >
                            <span className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7">
                                <RiLockPasswordLine className="tw-text-black tw-text-sm" />
                            </span>
                            Change Password
                        </button>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                navigate("/change-email");
                            }}
                            className="tw-flex tw-items-center tw-gap-2 tw-border-b-1 tw-w-full tw-text-left tw-p-2 tw-text-sm"
                        >
                            <span className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7">
                                <MdOutlineMail className="tw-text-black tw-text-sm" />
                            </span>
                            Change Email
                        </button>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                if (profileData?.verification_status) {
                                    setVerificationPopupVisible(true);
                                } else {
                                    navigate("/start-verification");
                                }
                            }}
                            className="tw-flex tw-items-center tw-gap-2 tw-border-b-1 tw-w-full tw-text-left tw-p-2 tw-text-sm"
                        >
                            <span className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7">
                                <MdOutlineVerified className="tw-text-black tw-font-thin tw-text-sm" />
                            </span>
                            Get Verified
                        </button>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                navigate("/saved-searches");
                            }}
                            className="tw-flex tw-items-center tw-gap-2 tw-border-b-1 tw-w-full tw-text-left tw-p-2 tw-text-sm"
                        >
                            <span className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7">
                                <MdOutlineAddToPhotos className="tw-text-black tw-font-thin tw-text-sm" />
                            </span>
                            Saved Searches
                        </button>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                navigate("/MyProperties");
                            }}
                            className="tw-flex tw-items-center tw-gap-2 tw-border-b-1 tw-w-full tw-text-left tw-p-2 tw-text-sm"
                        >
                            <span className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7">
                                <FaRegCreditCard className="tw-text-black tw-font-thin tw-text-sm" />
                            </span>
                            My Subscriptions
                        </button>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                navigate("/chat");
                            }}
                            className="tw-flex tw-items-center tw-gap-2 tw-border-b-1 tw-w-full tw-text-left tw-p-2 tw-text-sm"
                        >
                            <span className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7">
                                <RiCustomerService2Line className="tw-text-black tw-font-thin tw-text-sm" />
                            </span>
                            Customer Support
                        </button>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                navigate("/privacy-policy");
                            }}
                            className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-text-left tw-p-2 tw-text-sm"
                        >
                            <span className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7">
                                <MdOutlinePrivacyTip className="tw-text-black tw-text-sm tw-font-thin" />
                            </span>
                            Agreements & Policies.
                        </button>
                    </div>

                    {/* Logout Button */}
                    <div className="tw-border-t tw-border-gray-200">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                logout();
                            }}
                            className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-text-left tw-p-2 tw-text-sm tw-text-red-500 tw-font-semibold"
                        >
                            <span className="tw-bg-red-50 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7">
                                <FiLogOut className="tw-text-red-500 tw-text-sm" />
                            </span>
                            Log Out
                        </button>
                    </div>
                </div>
            )}

            {/* Notification Popup */}
            {isNotificationPopupOpen && <NotificationPopup setIsNotificationPopupOpen={setIsNotificationPopupOpen} />}
            {verificationPopupVisible && <VerificationPopup profileData={profileData} setVerificationPopupVisible={setVerificationPopupVisible} />}
            {verificationReason && (
                <VerificationRejectionReason
                    verificationStatus={profileData?.verification_status}
                    rejection_reason={profileData?.rejection_reason}
                    setVerificationPopupVisible={setVerificationPopupVisible}
                />
            )}
        </div>
    );
}
